// Copyright (c) Docugami, Inc. All rights reserved.

import { v4 as uuidv4 } from 'uuid';

export enum ObservableCollectionChangeType {
    Add,
    Remove,
    Modify,
    Reset
}

export interface ObservableChange<T> {
    type: ObservableCollectionChangeType;
    item?: T;
}

export type OnChangeCallback<T> = (change: ObservableChange<T>) => void;

export interface IObservableCollection<T> {
    registerListener(callback: OnChangeCallback<T>): () => void;
    items: T[];
}

interface RegistrationToken<T> {
    id: string;
    callback: OnChangeCallback<T>;
}

/**
 * Wraps an array and provides common APIs for manipulating the underlying data while
 * generating the appropriate change notifications based on the manipulations.
 */
export class ObservableArray<T> implements IObservableCollection<T> {
    private _storage: T[];
    private readonly _listeners: RegistrationToken<T>[] = []
    private readonly _comparator: (item1: T, item2: T) => boolean;

    public get storage(): T[] { return this._storage; }
    public get items(): T[] { return [...this._storage]; }

    constructor(storage: T[] = [], comparator: (item1: T, item2: T) => boolean = (item1, item2) => item1 === item2) {
        this._storage = storage;
        this._comparator = comparator;
    }

    registerListener(callback: OnChangeCallback<T>): () => void {
        const token = {
            id: uuidv4(),
            callback: callback
        };

        this._listeners.push(token);
        return () => this.unregister(token);
    }

    reset(...items: T[]) {
        this._storage = items;

        this.notifyListeners({
            type: ObservableCollectionChangeType.Reset
        });
    }

    push(...items: T[]): number {
        const size = this._storage.push(...items);

        for (const item of items) {
            this.notifyListeners({
                type: ObservableCollectionChangeType.Add,
                item: item
            });
        }

        return size;
    }

    pop(): T | undefined {
        const item = this._storage.pop();
        if (!!item) {
            this.notifyListeners({
                type: ObservableCollectionChangeType.Remove,
                item: item
            });
        }

        return item;
    }

    splice(start: number, deleteCount?: number): T[] {
        const items = this._storage.splice(start, deleteCount);

        for (const item of items) {
            this.notifyListeners({
                type: ObservableCollectionChangeType.Remove,
                item: item
            });
        }

        return items;
    }

    set(index: number, item: T) {
        const other = this._storage[index];
        this._storage[index] = item;

        if (this._comparator(other, item)) {
            // Modify
            this.notifyListeners({
                type: ObservableCollectionChangeType.Modify,
                item: item
            });
        }
        else {
            // Remove the old and add the new
            this.notifyListeners({
                type: ObservableCollectionChangeType.Remove,
                item: other
            });

            this.notifyListeners({
                type: ObservableCollectionChangeType.Add,
                item: item
            });
        }
    }

    addOrUpdate(item: T) {
        const index = this.indexOfItem(item);
        if (index === -1) {
            // Add
            this.push(item);
        }
        else {
            // Modify
            this.set(index, item);
        }
    }

    update(item: T) {
        const index = this.indexOfItem(item);
        if (index !== -1) {
            // Modify
            this.set(index, item);
        }
    }

    remove(...items: T[]) {
        for (const item of items) {
            const index = this.indexOfItem(item);
            if (index !== -1) {
                this.splice(index, 1);
            }
        }
    }

    removeByKey<Key extends keyof T>(key: Key, ...items: T[]) {
        for (const item of items) {
            const index = this.indexOf(key, item[key]);
            if (index !== -1) {
                this.splice(index, 1);
            }
        }
    }

    removeByKeyValue<Key extends keyof T>(key: Key, value: T[Key]) {
        const index = this.indexOf(key, value);
        if (index !== -1) {
            this.splice(index, 1);
        }
    }

    indexOfItem(item: T): number {
        return this._storage.findIndex(x => this._comparator(x, item));
    }

    indexOf<Key extends keyof T>(searchKey: Key, value: T[Key]): number {
        return this._storage.findIndex(x => x[searchKey] === value);
    }

    find<Key extends keyof T>(searchKey: Key, value: T[Key]): T | undefined {
        return this._storage.find(x => x[searchKey] === value);
    }

    get(index: number) {
        return this._storage[index];
    }

    private notifyListeners(change: ObservableChange<T>) {
        for (const token of this._listeners) {
            token.callback(change);
        }
    }

    private unregister(token: RegistrationToken<T>) {
        const index = this._listeners.findIndex(t => t.id === token.id);
        if (index !== -1) {
            this._listeners.splice(index, 1);
        }
    }
}
